import { createLogger } from './logger'

/**
 * Fire-and-forget helper for promises whose rejection should be logged but
 * not propagated to callers.
 *
 * Background: several mutation hooks (useBookmarks.toggleBookmark,
 * useReminders.setReminder, useTags.assignTag/removeTagAssignment,
 * useCategories.addCategory/updateCategory/deleteCategory) return the raw
 * mutateAsync promise. That's a deliberate API choice — callers that want
 * to toast or retry can .catch() it. But most callsites are keyboard
 * shortcut handlers, drag-to-reorder effects, and SelectionActionBar
 * buttons where the user expectation is fire-and-forget: realtime
 * invalidation reconciles state, an error is rare, and we just want it
 * visible in logs / Sentry rather than bubbling up as an
 * "Uncaught (in promise)" console error.
 *
 * Usage:
 *   voidAsync(toggleBookmark(urn), 'toggleBookmark')
 *   voidAsync(updateCategory(id, { name }), 'updateCategory', { id, name })
 *
 * The second arg is a short scope tag that shows up in the log prefix.
 * The third (optional) is structured context passed to log.error.
 */
const log = createLogger('void')

export function voidAsync(
  promise: Promise<unknown>,
  scope: string,
  context?: Record<string, unknown>,
): void {
  promise.catch((err: unknown) => {
    const message = err instanceof Error ? err.message : String(err)
    log.error(`${scope} failed: ${message}`, context ?? err)
  })
}
